import { supabase } from "./supabase";

const API_BASE =
  import.meta.env.VITE_API_URL || "";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${session?.access_token || ""}`,
  };
}

export async function generateWorkout(userId, context) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/generate-workout`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId, context }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to generate workout");
  }

  return res.json();
}

export async function getPostWorkoutInsight(
  userId,
  workout,
  generatedWorkout,
  newPrs
) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/coach-insights`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        action: "post-workout",
        user_id: userId,
        workout,
        generated_workout: generatedWorkout,
        new_prs: newPrs,
      }),
    });
  } catch (e) {
    console.warn("Post-workout insight network error:", e.message);
    return {
      headline: "Session complete.",
      insight: "Great work getting your training in today.",
      next_focus: "Keep building momentum.",
    };
  }

  if (!res.ok) {
    return {
      headline: "Session complete.",
      insight: "Great work getting your training in today.",
      next_focus: "Keep building momentum.",
    };
  }

  return res.json();
}

// Fetch today's generated workout (if any)
export async function fetchTodayWorkout(userId) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { data, error } = await supabase
    .from("generated_workouts")
    .select("*")
    .eq("user_id", userId)
    .gte("created_at", today.toISOString())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Failed to fetch today's workout:", error);
    return null;
  }
  return data;
}

// Mark a generated workout as accepted
export async function acceptGeneratedWorkout(workoutId) {
  const { error } = await supabase
    .from("generated_workouts")
    .update({ accepted: true })
    .eq("id", workoutId);

  if (error) console.error("Failed to accept workout:", error);
}

// Fetch weekly progress report
export async function getWeeklyReport(userId, { force = false } = {}) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/coach-insights`, {
      method: "POST",
      headers,
      body: JSON.stringify({ action: "weekly-report", user_id: userId, force }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to get report");
  }

  return res.json();
}

// Send a message to the AI coach chat
// Returns { message, workout?, meals? }
export async function sendChatMessage(userId, messages, context) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/coach-chat`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId, messages, context }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to get coach response");
  }

  return res.json();
}

// Streaming variant of sendChatMessage. Calls onDelta(visibleTextSoFar) as
// tokens arrive; resolves with the same shape as sendChatMessage. Hidden
// <<TAG>> blocks are suppressed from the live text; the final metadata line
// (<<<ELEVATE_META>>>{json}) carries the workout/meal cards.
// Throws on failure — callers fall back to sendChatMessage.
const META_SENTINEL = "<<<ELEVATE_META>>>";

function visibleText(acc) {
  return acc
    .replace(/\s*<<WORKOUT:[\s\S]*?>>/g, "")
    .replace(/\s*<<MEAL:[\s\S]*?>>/g, "")
    .replace(/\s*<<SAVE:\w+\|[\s\S]+?>>/g, "")
    .replace(/\s*<{2,3}[^>]*$/, ""); // hide a tag still being streamed
}

export async function sendChatMessageStream(userId, messages, context, onDelta) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/coach-chat`, {
    method: "POST",
    headers,
    body: JSON.stringify({ user_id: userId, messages, context, stream: true }),
  });
  if (!res.ok || !res.body) throw new Error("stream unavailable");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let acc = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    acc += decoder.decode(value, { stream: true });
    const cut = acc.indexOf(META_SENTINEL);
    onDelta?.(visibleText(cut === -1 ? acc : acc.slice(0, cut)).trimStart());
  }
  acc += decoder.decode();

  // Split off the metadata line
  let meta = {};
  const cut = acc.indexOf(META_SENTINEL);
  let raw = acc;
  if (cut !== -1) {
    raw = acc.slice(0, cut);
    try { meta = JSON.parse(acc.slice(cut + META_SENTINEL.length)); } catch { /* no cards */ }
  }
  const message = visibleText(raw).trim();
  if (!message) throw new Error("empty stream");
  return { message, workout: meta.workout || null, meals: meta.meals || null };
}

// Fetch proactive AI coaching insights (plateau detection, patterns, etc.)
export async function getProactiveInsights(userId) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/coach-insights`, {
      method: "POST",
      headers,
      body: JSON.stringify({ action: "proactive", user_id: userId }),
    });
  } catch (e) {
    console.warn("Proactive insights network error:", e.message);
    return { insights: [] };
  }

  if (!res.ok) return { insights: [] };
  return res.json();
}

// Get short form cues for an exercise. Cached in localStorage forever —
// form advice for "Barbell Squat" doesn't change, so each exercise costs
// one AI call per device, ever.
export async function getFormCues(exerciseName) {
  const cacheKey = `form_cues_${exerciseName.toLowerCase().replace(/[^a-z0-9]/g, "_")}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return cached;
  } catch { /* storage unavailable */ }

  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/coach-insights`, {
    method: "POST",
    headers,
    body: JSON.stringify({ action: "form-cues", exercise: exerciseName }),
  });
  if (!res.ok) throw new Error("Couldn't load form tips");
  const { cues } = await res.json();
  try { localStorage.setItem(cacheKey, cues); } catch { /* full */ }
  return cues;
}

// Parse a free-text food description into structured macro items via Claude
export async function parseFoodNlp(text) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/food`, {
      method: "POST",
      headers,
      body: JSON.stringify({ text }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to parse food description");
  }
  return res.json();
}

// Fetch today's morning briefing from pre-generated table
export async function fetchMorningBriefing(userId) {
  const today = new Date().toISOString().split("T")[0];
  const { data, error } = await supabase
    .from("morning_briefings")
    .select("*")
    .eq("user_id", userId)
    .eq("date", today)
    .maybeSingle();

  if (error) {
    // Table may not exist yet — don't crash
    if (error.message?.includes("does not exist")) return null;
    console.error("Failed to fetch morning briefing:", error);
    return null;
  }
  return data?.content || null;
}

// Generate morning briefing on-demand (fallback if cron hasn't run)
export async function generateMorningBriefing(userId) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/generate-content`, {
      method: "POST",
      headers,
      body: JSON.stringify({ type: "briefing", user_id: userId }),
    });
  } catch (e) {
    console.warn("Morning briefing generation error:", e.message);
    return null;
  }
  if (!res.ok) return null;
  return res.json();
}

// Fetch the latest weekly review
export async function fetchWeeklyReview(userId) {
  // Get current week start (Sunday)
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekStartStr = weekStart.toISOString().split("T")[0];

  // Also check last week
  const lastWeekStart = new Date(weekStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekStr = lastWeekStart.toISOString().split("T")[0];

  const { data, error } = await supabase
    .from("weekly_reviews")
    .select("*")
    .eq("user_id", userId)
    .in("week_start", [weekStartStr, lastWeekStr])
    .order("week_start", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (error.message?.includes("does not exist")) return null;
    console.error("Failed to fetch weekly review:", error);
    return null;
  }
  return data ? { ...data.content, week_start: data.week_start, generated_at: data.generated_at } : null;
}

// Generate weekly review on-demand
export async function generateWeeklyReview(userId, { force = false } = {}) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/generate-content`, {
      method: "POST",
      headers,
      body: JSON.stringify({ type: "weekly-review", user_id: userId, force }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to generate review");
  }
  return res.json();
}

// Fetch latest insight for a workout
export async function fetchWorkoutInsight(workoutId) {
  const { data, error } = await supabase
    .from("workout_insights")
    .select("*")
    .eq("workout_id", workoutId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Failed to fetch insight:", error);
    return null;
  }
  return data;
}

// =========== RUN TRAINING PLANS ===========

export async function generateRunPlan(userId, planConfig) {
  const headers = await getAuthHeaders();
  let res;
  try {
    res = await fetch(`${API_BASE}/api/generate-run-plan`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId, ...planConfig }),
    });
  } catch {
    throw new Error("Network error — check your connection and try again.");
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || "Failed to generate training plan");
  }

  return res.json();
}
